import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Quote, AlertTriangle, Lightbulb, TrendingUp,
  Truck, Pill, Fuel, Users, ArrowRight, MapPin, Target,
} from 'lucide-react';

const CAMPAIGN_VIDEO =
  'https://res.cloudinary.com/dn6stpnn2/video/upload/v1780668985/rakaictyhealth/campaignvid_dxmjdh.mp4';

const Campaign = () => {
  const navigate = useNavigate();

  const goSupport = () => {
    navigate('/support_us');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToVideo = () => {
    const el = document.getElementById('campaign-video');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const challenges = [
    'Government health facilities are roughly 20 km away from the communities that need them, across difficult terrain.',
    'High transport costs force families to choose between buying food or making a clinic visit — many choose food.',
    'Patients with chronic conditions miss their daily doses, conditions worsen, and complications turn fatal.',
  ];

  const solutions = [
    { icon: Pill, text: 'Deliver life-saving medication — Insulin, HIV treatment and TB drugs — directly to people’s homes.' },
    { icon: Users, text: 'Provide consistent follow-up care for patients living with chronic conditions.' },
    { icon: Truck, text: 'Run integrated community outreaches with health workers and local leaders, removing the transport barrier.' },
  ];

  const fundingUse = [
    { icon: Truck, title: 'An outreach service van', text: 'To transport health workers, medicines and supplies to remote outreach sites.' },
    { icon: Fuel, title: 'Fuel for outreaches', text: 'Keeping the van on the road and reaching the hardest-to-reach villages every month.' },
    { icon: Users, title: 'Health worker facilitation', text: 'Allowances and support for the health workers who deliver care on every outreach visit.' },
  ];

  return (
    <div className="campaign-page">
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet" />

      {/* ── Hero ── */}
      <section className="camp-hero">
        <div className="hero-bg-shapes">
          <div className="bg-shape s1" />
          <div className="bg-shape s2" />
          <div className="bg-shape s3" />
        </div>

        <div className="container hero-inner">
          <span className="hero-badge">Fundraising Campaign</span>
          <h1 className="hero-title">
            Join us Deliver Life-Saving Care Closer to Hard-to-Reach Communities in Rakai
          </h1>
          <p className="hero-subtitle">
            Help us bring insulin, HIV and TB treatment, and follow-up care closer to vulnerable
            people in rural Rakai District, Uganda — so no one misses a dose because a clinic is
            20&nbsp;kilometres away.
          </p>

          <div className="hero-goal">
            <Target size={18} />
            <span>Campaign goal: <strong>£50,000</strong></span>
          </div>

          <div className="hero-actions">
            <button className="btn-donate" onClick={goSupport}>
              <Heart size={18} /> Donate Now
            </button>
            <button className="btn-watch" onClick={scrollToVideo}>
              Watch the story <ArrowRight size={16} />
            </button>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      {/* ── Aisha's Story ── */}
      <section className="camp-section">
        <div className="container">
          <div className="story-card">
            <div className="story-quote-icon"><Quote size={28} /></div>
            <p className="story-text">
              “My name is <strong>Aisha Naluku</strong>, I am 14 years old and live in a remote village
              in Rakai district. Two years ago I was diagnosed with Type 1 Diabetes and needed daily
              insulin to survive. But the nearest health facility was over 20 kilometres away, and
              transport alone cost more than my family could afford. Some months I missed my clinic
              visits; other times I reduced my insulin doses to make them last longer. I became weak,
              missed school, and was frequently admitted with complications.”
            </p>
            <p className="story-text story-hope">
              “Then Rakai Community Health Initiative began delivering my insulin every month, at home.
              I am now healthy, back in school, and living with hope.”
            </p>
            <div className="story-attr">
              <MapPin size={15} /> Aisha Naluku · Rakai District, Uganda
            </div>
          </div>
        </div>
      </section>

      {/* ── Video ── */}
      <section className="camp-video-section" id="campaign-video">
        <div className="container">
          <div className="section-head">
            <span className="section-badge">Watch</span>
            <h2 className="section-title">See the campaign in their own words</h2>
          </div>
          <div className="video-frame">
            <video
              controls
              preload="metadata"
              playsInline
              className="campaign-video"
            >
              <source src={CAMPAIGN_VIDEO} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* ── Challenge ── */}
      <section className="camp-section">
        <div className="container">
          <div className="section-head">
            <span className="section-badge challenge">The Challenge</span>
            <h2 className="section-title">Care is too far, and the cost is too high</h2>
            <p className="section-desc">
              Many children and adults in Rakai live with chronic conditions — diabetes, hypertension,
              HIV and TB — in hard-to-reach areas where routine medication should never be optional.
            </p>
          </div>
          <div className="row g-4 justify-content-center">
            {challenges.map((c, i) => (
              <div key={i} className="col-lg-4 col-md-6">
                <div className="info-card challenge-card">
                  <div className="info-icon challenge-icon"><AlertTriangle size={22} /></div>
                  <p className="info-text">{c}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Solution ── */}
      <section className="camp-section solution-bg">
        <div className="container">
          <div className="section-head">
            <span className="section-badge solution">Our Solution</span>
            <h2 className="section-title">Bringing healthcare to the people who need it most</h2>
            <p className="section-desc">
              We are a community-driven organization bridging the gap between health facilities and
              underserved populations. Together with health workers and local leaders, we run
              integrated outreaches that take essential services directly to remote villages.
            </p>
          </div>
          <div className="row g-4 justify-content-center">
            {solutions.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="col-lg-4 col-md-6">
                  <div className="info-card solution-card">
                    <div className="info-icon solution-icon"><Icon size={22} /></div>
                    <p className="info-text">{s.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Long-term impact / What your gift buys ── */}
      <section className="camp-section">
        <div className="container">
          <div className="section-head">
            <span className="section-badge impact"><TrendingUp size={14} /> Lasting Impact</span>
            <h2 className="section-title">What your donation makes possible</h2>
            <p className="section-desc">
              Your gift toward our <strong>£50,000</strong> goal equips RACOHI with the means to keep
              reaching remote communities, month after month.
            </p>
          </div>
          <div className="row g-4 justify-content-center">
            {fundingUse.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="col-lg-4 col-md-6">
                  <div className="impact-card">
                    <div className="impact-icon"><Icon size={24} /></div>
                    <h4 className="impact-title">{f.title}</h4>
                    <p className="impact-text">{f.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="camp-cta">
        <div className="container text-center">
          <Lightbulb size={32} className="cta-spark" />
          <h2 className="cta-title">No one should miss a dose because of a transport fee.</h2>
          <p className="cta-sub">
            Join us in delivering life-saving care closer to home. Every gift moves us closer to
            healthier, hope-filled communities in Rakai.
          </p>
          <button className="btn-donate large" onClick={goSupport}>
            <Heart size={18} /> Support This Campaign
          </button>
        </div>
      </section>

      <style jsx>{`
        :root {
          --primary-blue: #2563eb;
          --secondary-blue: #1e40af;
          --accent-teal: #06b6d4;
          --accent-orange: #f59e0b;
          --danger: #dc2626;
          --success: #10b981;
          --text-gray: #6b7280;
          --light-gray: #f8fafc;
        }

        .campaign-page { min-height: 100vh; background: var(--light-gray); }

        /* ── Hero ── */
        .camp-hero {
          position: relative;
          padding: 80px 0 0;
          background: linear-gradient(135deg, #0e7490 0%, var(--accent-teal) 45%, var(--primary-blue) 100%);
          color: white;
          overflow: hidden;
        }
        .hero-inner {
          position: relative; z-index: 2; text-align: center;
          padding-top: 3.5rem; padding-bottom: 4.5rem; max-width: 820px;
        }
        .hero-bg-shapes { position: absolute; inset: 0; z-index: 1; pointer-events: none; }
        .bg-shape { position: absolute; border-radius: 50%; background: rgba(255,255,255,0.06); }
        .s1 { width: 340px; height: 340px; top: -100px; left: -60px; }
        .s2 { width: 200px; height: 200px; bottom: 10px; right: -40px; background: rgba(255,255,255,0.04); }
        .s3 { width: 120px; height: 120px; top: 50px; right: 30%; background: rgba(255,255,255,0.05); }

        .hero-badge {
          display: inline-block; background: rgba(255,255,255,0.15); color: white;
          padding: 0.4rem 1.2rem; border-radius: 50px; font-size: 0.8rem; font-weight: 600;
          letter-spacing: 1.2px; text-transform: uppercase; margin-bottom: 1.2rem;
          border: 1px solid rgba(255,255,255,0.25);
        }
        .hero-title {
          font-size: clamp(2rem, 4.6vw, 3.2rem); font-weight: 800;
          margin-bottom: 1.1rem; line-height: 1.12;
        }
        .hero-subtitle {
          font-size: 1.12rem; opacity: 0.9; max-width: 640px; margin: 0 auto;
          line-height: 1.65;
        }
        .hero-goal {
          display: inline-flex; align-items: center; gap: 0.5rem;
          margin-top: 1.6rem; padding: 0.5rem 1.2rem; border-radius: 50px;
          background: rgba(245,158,11,0.18); border: 1px solid rgba(245,158,11,0.5);
          font-size: 1rem;
        }
        .hero-goal strong { color: #ffe9c2; }
        .hero-actions {
          display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;
          margin-top: 1.8rem;
        }
        .btn-donate {
          display: inline-flex; align-items: center; gap: 0.5rem;
          background: var(--accent-orange); color: white; border: none;
          padding: 0.85rem 1.8rem; border-radius: 50px; font-weight: 700; font-size: 1rem;
          cursor: pointer; transition: all 0.25s ease; box-shadow: 0 8px 24px rgba(245,158,11,0.35);
        }
        .btn-donate:hover { background: #d97706; transform: translateY(-2px); }
        .btn-donate.large { padding: 1rem 2.4rem; font-size: 1.05rem; }
        .btn-watch {
          display: inline-flex; align-items: center; gap: 0.45rem;
          background: rgba(255,255,255,0.12); color: white;
          border: 1px solid rgba(255,255,255,0.4);
          padding: 0.85rem 1.6rem; border-radius: 50px; font-weight: 600; font-size: 1rem;
          cursor: pointer; transition: all 0.25s ease;
        }
        .btn-watch:hover { background: rgba(255,255,255,0.22); transform: translateY(-2px); }

        .hero-wave { position: relative; z-index: 2; line-height: 0; margin-bottom: -2px; }
        .hero-wave svg { width: 100%; height: 60px; display: block; }

        /* ── Sections ── */
        .camp-section { padding: 4.5rem 0; }
        .solution-bg { background: white; }

        .section-head { text-align: center; max-width: 720px; margin: 0 auto 3rem; }
        .section-badge {
          display: inline-flex; align-items: center; gap: 0.35rem;
          background: rgba(37,99,235,0.1); color: var(--primary-blue);
          padding: 0.4rem 1.1rem; border-radius: 50px; font-size: 0.8rem; font-weight: 700;
          letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 1rem;
        }
        .section-badge.challenge { background: rgba(220,38,38,0.1); color: var(--danger); }
        .section-badge.solution { background: rgba(16,185,129,0.12); color: #047857; }
        .section-badge.impact { background: rgba(245,158,11,0.14); color: #b45309; }
        .section-title {
          font-size: clamp(1.8rem, 3.4vw, 2.6rem); font-weight: 800;
          color: var(--secondary-blue); line-height: 1.18; margin-bottom: 1rem;
        }
        .section-desc { font-size: 1.08rem; color: var(--text-gray); line-height: 1.65; margin: 0; }

        /* ── Story card ── */
        .story-card {
          position: relative; background: white; max-width: 880px; margin: 0 auto;
          border-radius: 1.5rem; padding: 3rem 2.5rem 2.5rem;
          box-shadow: 0 10px 40px rgba(0,0,0,0.08);
          border-left: 6px solid var(--accent-teal);
        }
        .story-quote-icon {
          position: absolute; top: -22px; left: 2.2rem;
          width: 48px; height: 48px; border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-teal), var(--primary-blue));
          color: white; display: flex; align-items: center; justify-content: center;
          box-shadow: 0 8px 20px rgba(37,99,235,0.3);
        }
        .story-text { font-size: 1.12rem; color: #374151; line-height: 1.75; margin-bottom: 1.1rem; }
        .story-hope { color: var(--secondary-blue); font-weight: 600; }
        .story-attr {
          display: inline-flex; align-items: center; gap: 0.4rem;
          color: var(--accent-orange); font-weight: 700; font-size: 0.92rem; margin-top: 0.5rem;
        }

        /* ── Video ── */
        .camp-video-section { padding: 4.5rem 0; background: white; }
        .video-frame {
          max-width: 900px; margin: 0 auto; border-radius: 1.25rem; overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.18);
          background: #000; aspect-ratio: 16 / 9;
        }
        .campaign-video { width: 100%; height: 100%; display: block; object-fit: contain; background: #000; }

        /* ── Info cards (challenge / solution) ── */
        .info-card {
          background: white; border-radius: 1.25rem; padding: 2rem 1.6rem; height: 100%;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06); transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .solution-bg .info-card { background: var(--light-gray); }
        .info-card:hover { transform: translateY(-6px); box-shadow: 0 18px 48px rgba(0,0,0,0.1); }
        .info-icon {
          width: 52px; height: 52px; border-radius: 14px; display: flex;
          align-items: center; justify-content: center; margin-bottom: 1.1rem; color: white;
        }
        .challenge-icon { background: linear-gradient(135deg, #ef4444, #dc2626); }
        .solution-icon { background: linear-gradient(135deg, var(--success), #059669); }
        .info-text { color: #374151; font-size: 1rem; line-height: 1.6; margin: 0; }

        /* ── Impact cards ── */
        .impact-card {
          background: white; border-radius: 1.25rem; padding: 2.2rem 1.8rem; height: 100%;
          text-align: center; box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          transition: transform 0.3s ease, box-shadow 0.3s ease; position: relative; overflow: hidden;
        }
        .impact-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, var(--accent-orange), var(--accent-teal));
        }
        .impact-card:hover { transform: translateY(-6px); box-shadow: 0 18px 48px rgba(0,0,0,0.1); }
        .impact-icon {
          width: 64px; height: 64px; margin: 0 auto 1.2rem; border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-orange), #fbbf24);
          display: flex; align-items: center; justify-content: center; color: white;
          box-shadow: 0 8px 20px rgba(245,158,11,0.3);
        }
        .impact-title { font-size: 1.15rem; font-weight: 700; color: var(--secondary-blue); margin-bottom: 0.6rem; }
        .impact-text { color: var(--text-gray); font-size: 0.95rem; line-height: 1.6; margin: 0; }

        /* ── CTA ── */
        .camp-cta {
          padding: 5rem 0;
          background: linear-gradient(135deg, var(--secondary-blue), var(--primary-blue) 55%, #0e7490);
          color: white;
        }
        .cta-spark { color: #ffe9c2; margin-bottom: 1rem; }
        .cta-title {
          font-size: clamp(1.7rem, 3.4vw, 2.5rem); font-weight: 800;
          max-width: 760px; margin: 0 auto 1rem; line-height: 1.25;
        }
        .cta-sub { font-size: 1.1rem; opacity: 0.9; max-width: 620px; margin: 0 auto 2rem; line-height: 1.6; }

        @media (max-width: 768px) {
          .camp-hero { padding-top: 70px; }
          .hero-inner { padding-top: 2.5rem; padding-bottom: 3.5rem; }
          .camp-section, .camp-video-section { padding: 3.2rem 0; }
          .story-card { padding: 2.5rem 1.5rem 2rem; }
        }
      `}</style>
    </div>
  );
};

export default Campaign;
